import * as THREE from 'three';
import { AppState } from '../core/constants.js';
import { TreeSystem } from './treeSystem.js';
import { PhotoCloud } from './photoCloud.js';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export class SceneController {
  constructor(rootElement) {
    this.rootElement = rootElement;
    this.scene = new THREE.Scene();
    this.scene.background = null;
    this.scene.fog = new THREE.Fog(0xfce5f3, 8, 24);

    this.cameraTarget = new THREE.Vector3(0, 3, 0);
    this.orbitRadius = 8.8;
    this.orbitYaw = 0;
    this.orbitPitch = 0.2;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(rootElement.clientWidth, rootElement.clientHeight);
    rootElement.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      48,
      rootElement.clientWidth / rootElement.clientHeight,
      0.1,
      100
    );

    this.ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
    this.keyLight = new THREE.DirectionalLight(0xffffff, 1.5);
    this.keyLight.position.set(3, 8, 5);
    this.fillLight = new THREE.DirectionalLight(0xffffff, 0.9);
    this.fillLight.position.set(-5, 2, -4);

    this.scene.add(this.ambientLight);
    this.scene.add(this.keyLight);
    this.scene.add(this.fillLight);

    this.treeSystem = new TreeSystem(this.scene);
    this.photoCloud = new PhotoCloud(this.scene);
  }

  resize() {
    const { clientWidth, clientHeight } = this.rootElement;
    if (!clientWidth || !clientHeight) {
      return;
    }
    this.camera.aspect = clientWidth / clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(clientWidth, clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }

  applySeason(profile) {
    this.treeSystem.applySeason(profile);
    this.scene.fog.color.setHex(profile.fogColor);
    this.ambientLight.color.setHex(profile.ambientColor);
  }

  applyRotateDelta(dx, dy) {
    this.orbitYaw += dx * 1.4;
    this.orbitPitch += dy * 1.2;
    this.orbitPitch = clamp(this.orbitPitch, -0.1, 0.85);
  }

  async loadPhotos(files) {
    await this.photoCloud.setFiles(files);
  }

  photoCount() {
    return this.photoCloud.count();
  }

  pickPhotoByNdc(ndcX, ndcY) {
    return this.photoCloud.pickPhoto(this.camera, ndcX, ndcY);
  }

  update({ state, focusedPhotoIndex, seasonProfile, elapsedSeconds, deltaSeconds }) {
    if (state === AppState.DISPERSED) {
      this.orbitYaw += deltaSeconds * 0.14;
    }

    const cameraRadius = state === AppState.PHOTO_FOCUS ? 7.2 : this.orbitRadius;
    const targetY = state === AppState.PHOTO_FOCUS ? 3.2 : 3;

    const x = Math.sin(this.orbitYaw) * Math.cos(this.orbitPitch) * cameraRadius;
    const z = Math.cos(this.orbitYaw) * Math.cos(this.orbitPitch) * cameraRadius;
    const y = targetY + Math.sin(this.orbitPitch) * cameraRadius * 0.8;

    this.camera.position.set(x, y, z);
    this.camera.lookAt(this.cameraTarget);

    this.treeSystem.update(state, seasonProfile, elapsedSeconds, deltaSeconds);
    this.photoCloud.update(state, focusedPhotoIndex, this.camera, elapsedSeconds);
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    this.treeSystem.dispose();
    this.photoCloud.dispose();
    this.renderer.dispose();
  }
}
